import seaAreasUrl from '@/assets/sea-areas.json?url';

/**
 * `sea-areas.json` is Natural Earth's named marine areas plus its 29 largest lakes (public
 * domain, no attribution needed), cut to ~200 KB. It ships in the bundle, so this works at sea
 * with no signal. A `.json` asset is outside the PWA precache, so web users only fetch it the
 * first time a position is offshore. Regenerate from github.com/nvkelso/natural-earth-vector
 * `geojson/` with:
 *
 *   npx mapshaper@0.6.121 -i ne_10m_geography_marine_polys.geojson ne_10m_lakes.geojson \
 *     combine-files precision=0.01 -filter 'scalerank <= 1' target=ne_10m_lakes \
 *     -each 'name = featurecla != "ocean" || name == name.toUpperCase() ? name_en || name : name' \
 *       target=ne_10m_geography_marine_polys \
 *     -merge-layers force target='*' name=sea-areas -filter '!!name' -filter-fields name \
 *     -simplify 5% keep-shapes -filter remove-empty -o sea-areas.json format=geojson precision=0.01
 */

type Ring = [number, number][];
type Area = {
  properties: { name: string };
  geometry:
    | { type: 'Polygon'; coordinates: Ring[] }
    | { type: 'MultiPolygon'; coordinates: Ring[][] };
};

// Ray casting on raw lon/lat: fine because Natural Earth splits polygons at ±180°.
const inRing = (lon: number, lat: number, ring: Ring) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

let areas: Promise<Area[]> | undefined;

/**
 * The named sea, strait, bay or large lake a point lies in, e.g. "Florida Strait",
 * "Chesapeake Bay", "North Atlantic Ocean", "Lake Michigan"; `undefined` on land.
 */
// ponytail: borders are simplified to ~1 km, so within ~1 km of shore a point can miss or land
// in the neighbouring area. Nominatim names a town that close in, so the field rarely gets here.
export const seaArea = async (lat: number, lon: number): Promise<string | undefined> => {
  try {
    areas ??= fetch(seaAreasUrl)
      .then((res) => res.json())
      .then((fc: { features: Area[] }) => fc.features);
    const hit = (await areas).find(({ geometry: g }) =>
      (g.type === 'Polygon' ? [g.coordinates] : g.coordinates).some(
        ([outer, ...holes]) => inRing(lon, lat, outer) && !holes.some((h) => inRing(lon, lat, h)),
      ),
    );
    return hit?.properties.name;
  } catch {
    areas = undefined; // try the file again next time
    return undefined;
  }
};
